import {
  isLegacyPilotFieldId,
  isUfClientFieldId,
} from '~/features/fields/isLegacyPilotFieldId';
import {
  TargetEntity,
  TargetEntityDetails,
} from '~/features/x-ray/types/entity';
import { intlService } from '~/i18n/IntlService';

import { getClientFieldById } from '../fields/getClientFieldById';
import { getFields } from '../fields/getFields';

interface Request {
  entity: TargetEntity;
}

const fieldNotFoundError = () =>
  new Error(
    intlService.getIntl().formatMessage({
      id: 'resolvePromptModelTargetEntityDetails.fieldNotFound',
      defaultMessage: 'Field not found or has an unsupported type',
      description:
        'Error message when a field is missing or its type cannot be resolved',
    }),
  );

async function resolveLegacyFieldDetails(
  id: string,
  type: TargetEntity['type'],
): Promise<TargetEntityDetails> {
  const { results } = await getFields({ ids: [Number(id)] });
  const field = results[0];
  if (!field) {
    throw fieldNotFoundError();
  }

  return {
    id: String(field.id),
    label: field.label,
    type,
  };
}

async function resolveUfFieldDetails(
  id: string,
  type: TargetEntity['type'],
): Promise<TargetEntityDetails> {
  const field = await getClientFieldById({ id });
  if (!field) {
    throw fieldNotFoundError();
  }

  return {
    id: String(field.id),
    label: field.label,
    type,
  };
}

export const resolvePromptModelTargetEntityDetails = async ({
  entity,
}: Request): Promise<TargetEntityDetails> => {
  const { id, type } = entity;
  switch (type) {
    case 'field': {
      if (isLegacyPilotFieldId(id)) {
        return resolveLegacyFieldDetails(id, type);
      }

      if (isUfClientFieldId(id)) {
        return resolveUfFieldDetails(id, type);
      }

      throw fieldNotFoundError();
    }
    default:
      throw new Error(
        intlService.getIntl().formatMessage({
          id: 'resolvePromptModelTargetEntityDetails.entityTypeNotSupported',
          defaultMessage: 'Entity type not supported',
          description: 'Error message when entity type is not supported',
        }),
      );
  }
};
